import { Banner, SegmentedControl, Typography } from '@neo4j-ndl/react';
import { Squares2X2IconOutline, TableCellsIconOutline } from '@neo4j-ndl/react/icons';
import { ACTION, consoleApi, usePermissions, useSetting, useUnsafeAppContext } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { Page, SearchField } from '@nx/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';

import { NewProjectList } from './project-list';
import { NewProjectTable } from './project-table';

export function Projects() {
  const { activeOrgId } = useUnsafeAppContext();
  const {
    error,
    data: projects = [],
    isLoading,
  } = consoleApi.useListOrganizationProjectsQuery(
    isNotNullish(activeOrgId) ? { organizationId: activeOrgId } : skipToken,
  );
  const [filter, setFilter] = useState('');

  const [projectDisplayType, setProjectDisplayType] = useSetting('console', 'projectDisplay');

  const { isAllowed } = usePermissions();
  const allowedProjects = useMemo(
    () => projects.filter((project) => isAllowed(ACTION.READ, `namespaces/${project.id}`)),
    [projects, isAllowed],
  );

  if (error) {
    return <Banner type="danger" title="API Error" description={JSON.stringify(error, null, 2)} isCloseable={false} />;
  }

  return (
    <Page contentType="light">
      <Typography variant="h2">Projects</Typography>
      <div className="flex items-center justify-between gap-4">
        <SearchField
          className="max-w-80 grow"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          htmlAttributes={{ 'aria-label': 'Search for project' }}
        />
        <SegmentedControl onChange={setProjectDisplayType} hasOnlyIcons size="small" selected={projectDisplayType}>
          <SegmentedControl.Item
            value="list"
            htmlAttributes={{
              'aria-label': 'View projects in List view',
            }}
          >
            <Squares2X2IconOutline className="size-4" />
          </SegmentedControl.Item>
          <SegmentedControl.Item
            value="table"
            htmlAttributes={{
              'aria-label': 'View projects in Table view',
            }}
          >
            <TableCellsIconOutline className="size-4" />
          </SegmentedControl.Item>
        </SegmentedControl>
      </div>
      {projectDisplayType === 'list' && (
        <NewProjectList projects={allowedProjects} filter={filter} isLoading={isLoading} />
      )}
      {projectDisplayType === 'table' && (
        <NewProjectTable projects={allowedProjects} filter={filter} setFilter={setFilter} isLoading={isLoading} />
      )}
    </Page>
  );
}
